import React, { useState } from 'react';
import BadgeAvatar from '../components/BadgeAvatar';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const CreateCaseStudyNew = ({ user, logout }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('manual');
  const [loading, setLoading] = useState(false);

  // Manual form state
  const [manualForm, setManualForm] = useState({
    title: '',
    description: '',
    content: '',
    difficulty: 'simple',
    tags: '',
    discussion_period_days: 14,
    solution_content: ''
  });

  // Guided wizard state
  const [guidedForm, setGuidedForm] = useState({
    signs_symptoms: '',
    tongue_pulse: '',
    points_used: '',
    model: 'gpt-4o-mini',
    difficulty: 'simple'
  });

  // Full wizard state
  const [fullForm, setFullForm] = useState({
    tcm_diagnosis: '',
    additional_info: '',
    model: 'gpt-4o-mini',
    difficulty: 'simple'
  });

  // Generated content state
  const [generatedContent, setGeneratedContent] = useState(null);

  const aiModels = [
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Fast & Cost-effective)', provider: 'OpenAI' },
    { value: 'gpt-4o', label: 'GPT-4o (Balanced)', provider: 'OpenAI' },
    { value: 'gpt-5', label: 'GPT-5 (Most Advanced)', provider: 'OpenAI' },
    { value: 'claude-3-7-sonnet', label: 'Claude 3.7 Sonnet (High Quality)', provider: 'Anthropic' },
    { value: 'claude-4-sonnet', label: 'Claude 4 Sonnet (Latest)', provider: 'Anthropic' },
    { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash (Fast)', provider: 'Google' },
    { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro (Advanced)', provider: 'Google' }
  ];

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!manualForm.title || !manualForm.description || !manualForm.content) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const tags = manualForm.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      const response = await api.post('/case-studies', {
        ...manualForm,
        tags
      });

      toast.success('Case study created successfully!');
      navigate(`/case-study/${response.data.id}`);
    } catch (error) {
      console.error('Error creating case study:', error);
      toast.error('Failed to create case study');
    } finally {
      setLoading(false);
    }
  };

  const handleGuidedGenerate = async () => {
    if (!guidedForm.signs_symptoms || !guidedForm.tongue_pulse || !guidedForm.points_used) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const pointsArray = guidedForm.points_used
        .split(',')
        .map(p => p.trim())
        .filter(p => p.length > 0);

      const response = await api.post('/ai/generate-guided', {
        signs_symptoms: guidedForm.signs_symptoms,
        tongue_pulse: guidedForm.tongue_pulse,
        points_used: pointsArray,
        model: guidedForm.model,
        difficulty: guidedForm.difficulty
      });

      setGeneratedContent(response.data);
      toast.success('Case study generated! Review and edit if needed.');
    } catch (error) {
      console.error('Error generating case study:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate case study');
    } finally {
      setLoading(false);
    }
  };

  const handleFullGenerate = async () => {
    if (!fullForm.tcm_diagnosis && !fullForm.additional_info) {
      toast.error('Please provide at least TCM diagnosis or additional information');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/ai/generate-full', {
        tcm_diagnosis: fullForm.tcm_diagnosis,
        additional_info: fullForm.additional_info,
        model: fullForm.model,
        difficulty: fullForm.difficulty
      });

      setGeneratedContent(response.data);
      toast.success('Case study generated! Review and edit if needed.');
    } catch (error) {
      console.error('Error generating case study:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate case study');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGenerated = async () => {
    if (!generatedContent) return;

    setLoading(true);
    try {
      const response = await api.post('/case-studies', {
        title: generatedContent.title,
        description: generatedContent.description,
        content: generatedContent.content,
        difficulty: activeTab === 'guided' ? guidedForm.difficulty : fullForm.difficulty,
        tags: generatedContent.tags || [],
        discussion_period_days: 14,
        solution_content: ''
      });

      toast.success('Case study saved successfully!');
      navigate(`/case-study/${response.data.id}`);
    } catch (error) {
      console.error('Error saving case study:', error);
      toast.error('Failed to save case study');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50'>
      {/* Header */}
      <header className='bg-white shadow-sm border-b border-gray-200'>
        <div className='container mx-auto px-4 py-4'>
          <div className='flex items-center justify-between'>
            <Link to='/dashboard' className='flex items-center space-x-3'>
              <div className='w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center'>
                <svg className='w-6 h-6 text-white' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253' />
                </svg>
              </div>
              <span className='text-xl font-bold text-gray-900' style={{ fontFamily: 'Playfair Display, serif' }}>Case Study Club</span>
            </Link>
            <div className='flex items-center space-x-4'>
              <Link to={`/profile/${user.id}`}>
                <img
                  src={user.picture || 'https://via.placeholder.com/40'}
                  alt={user.name}
                  className='w-10 h-10 rounded-full border-2 border-teal-500 hover:border-teal-600 transition-all cursor-pointer'
                />
              </Link>
              <Button onClick={logout} variant='outline' className='border-gray-300 text-gray-700 hover:bg-gray-100 rounded-full px-6'>
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Continued in next file due to length */}
      <div className='container mx-auto px-4 py-8 max-w-5xl'>
        <div className='bg-white rounded-3xl p-8 shadow-lg'>
          <div className='mb-8'>
            <h1 className='text-4xl font-bold text-gray-900 mb-2' style={{ fontFamily: 'Playfair Display, serif' }}>
              Create Case Study
            </h1>
            <p className='text-gray-600'>Choose your preferred method to create a case study.</p>
          </div>

          <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setGeneratedContent(null); }} className='w-full'>
            <TabsList className={`grid w-full ${user?.is_study_master && !user?.is_admin ? 'grid-cols-1' : 'grid-cols-3'} mb-8 bg-gray-100 p-1 rounded-xl`}>
              <TabsTrigger value='manual' className='rounded-lg' data-testid='tab-manual'>
                Manual Entry
              </TabsTrigger>
              {!user?.is_study_master || user?.is_admin ? (
                <>
                  <TabsTrigger value='guided' className='rounded-lg' data-testid='tab-guided'>
                    Guided Wizard
                  </TabsTrigger>
                  <TabsTrigger value='full' className='rounded-lg' data-testid='tab-full'>
                    Full Generation
                  </TabsTrigger>
                </>
              ) : null}
            </TabsList>

            {/* Manual Entry Tab */}
            <TabsContent value='manual'>
              <form onSubmit={handleManualSubmit} className='space-y-6'>
                <div>
                  <Label htmlFor='title' className='text-sm font-semibold text-gray-700 mb-2 block'>Title *</Label>
                  <Input
                    id='title'
                    data-testid='manual-title'
                    value={manualForm.title}
                    onChange={(e) => setManualForm({ ...manualForm, title: e.target.value })}
                    placeholder='Enter case study title'
                    className='border-gray-300 focus:border-teal-500 rounded-xl'
                    required
                  />
                </div>

                <div>
                  <Label htmlFor='description' className='text-sm font-semibold text-gray-700 mb-2 block'>Description *</Label>
                  <Textarea
                    id='description'
                    data-testid='manual-description'
                    value={manualForm.description}
                    onChange={(e) => setManualForm({ ...manualForm, description: e.target.value })}
                    placeholder='Brief description'
                    className='border-gray-300 focus:border-teal-500 rounded-xl min-h-[100px]'
                    required
                  />
                </div>

                <div>
                  <Label htmlFor='content' className='text-sm font-semibold text-gray-700 mb-2 block'>Content *</Label>
                  <Textarea
                    id='content'
                    data-testid='manual-content'
                    value={manualForm.content}
                    onChange={(e) => setManualForm({ ...manualForm, content: e.target.value })}
                    placeholder='Full case study content'
                    className='border-gray-300 focus:border-teal-500 rounded-xl min-h-[300px]'
                    required
                  />
                </div>

                <div>
                  <Label className='text-sm font-semibold text-gray-700 mb-2 block'>Difficulty Level *</Label>
                  <div className='flex gap-4'>
                    <label className='flex items-center space-x-2 cursor-pointer'>
                      <input
                        type='radio'
                        value='simple'
                        checked={manualForm.difficulty === 'simple'}
                        onChange={(e) => setManualForm({ ...manualForm, difficulty: e.target.value })}
                        className='text-teal-600'
                      />
                      <span className='difficulty-simple px-4 py-2 rounded-full text-sm font-medium'>Simple</span>
                    </label>
                    <label className='flex items-center space-x-2 cursor-pointer'>
                      <input
                        type='radio'
                        value='complex'
                        checked={manualForm.difficulty === 'complex'}
                        onChange={(e) => setManualForm({ ...manualForm, difficulty: e.target.value })}
                        className='text-teal-600'
                      />
                      <span className='difficulty-complex px-4 py-2 rounded-full text-sm font-medium'>Complex</span>
                    </label>
                  </div>
                </div>

                <div>
                  <Label htmlFor='tags' className='text-sm font-semibold text-gray-700 mb-2 block'>Tags (comma-separated)</Label>
                  <Input
                    id='tags'
                    value={manualForm.tags}
                    onChange={(e) => setManualForm({ ...manualForm, tags: e.target.value })}
                    placeholder='e.g., pain management, digestive issues'
                    className='border-gray-300 focus:border-teal-500 rounded-xl'
                  />
                </div>

                <div>
                  <Label htmlFor='solution' className='text-sm font-semibold text-gray-700 mb-2 block'>Solution (Optional)</Label>
                  <Textarea
                    id='solution'
                    value={manualForm.solution_content}
                    onChange={(e) => setManualForm({ ...manualForm, solution_content: e.target.value })}
                    placeholder='Solution that will be revealed after discussion period'
                    className='border-gray-300 focus:border-teal-500 rounded-xl min-h-[150px]'
                  />
                </div>

                <div className='flex justify-between pt-6 border-t border-gray-200'>
                  <Link to='/dashboard'>
                    <Button type='button' variant='outline' className='rounded-full px-8'>Cancel</Button>
                  </Link>
                  <Button
                    type='submit'
                    disabled={loading}
                    data-testid='submit-manual'
                    className='bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-full px-8'
                  >
                    {loading ? 'Creating...' : 'Create Case Study'}
                  </Button>
                </div>
              </form>
            </TabsContent>

            {/* Note: The Guided and Full wizard tabs would continue here but are too long for single file */}
            {/* Implementing abbreviated versions */}
            
            <TabsContent value='guided'>
              <div className='bg-teal-50 border border-teal-200 rounded-xl p-4 mb-6'>
                <p className='text-sm text-teal-800'>
                  <strong>Guided Wizard:</strong> Enter clinical observations and points. System generates complete case study with point explanations.
                </p>
              </div>
              <div className='space-y-6'>
                <div>
                  <Label className='text-sm font-semibold text-gray-700 mb-2 block'>Model</Label>
                  <Select value={guidedForm.model} onValueChange={(value) => setGuidedForm({ ...guidedForm, model: value })}>
                    <SelectTrigger className='w-full border-gray-300 rounded-xl'>
                      <SelectValue placeholder='Select AI model' />
                    </SelectTrigger>
                    <SelectContent>
                      {aiModels.map(model => (
                        <SelectItem key={model.value} value={model.value}>
                          {model.label} ({model.provider})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className='text-sm font-semibold text-gray-700 mb-2 block'>Difficulty</Label>
                  <div className='flex gap-4'>
                    <label className='flex items-center space-x-2 cursor-pointer'>
                      <input type='radio' value='simple' checked={guidedForm.difficulty === 'simple'} onChange={(e) => setGuidedForm({ ...guidedForm, difficulty: e.target.value })} />
                      <span className='difficulty-simple px-4 py-2 rounded-full text-sm font-medium'>Simple</span>
                    </label>
                    <label className='flex items-center space-x-2 cursor-pointer'>
                      <input type='radio' value='complex' checked={guidedForm.difficulty === 'complex'} onChange={(e) => setGuidedForm({ ...guidedForm, difficulty: e.target.value })} />
                      <span className='difficulty-complex px-4 py-2 rounded-full text-sm font-medium'>Complex</span>
                    </label>
                  </div>
                </div>

                <div>
                  <Label className='text-sm font-semibold text-gray-700 mb-2 block'>Signs & Symptoms *</Label>
                  <Textarea
                    value={guidedForm.signs_symptoms}
                    onChange={(e) => setGuidedForm({ ...guidedForm, signs_symptoms: e.target.value })}
                    placeholder='Describe signs and symptoms'
                    className='border-gray-300 rounded-xl min-h-[100px]'
                  />
                </div>

                <div>
                  <Label className='text-sm font-semibold text-gray-700 mb-2 block'>Tongue & Pulse *</Label>
                  <Textarea
                    value={guidedForm.tongue_pulse}
                    onChange={(e) => setGuidedForm({ ...guidedForm, tongue_pulse: e.target.value })}
                    placeholder='Describe tongue and pulse'
                    className='border-gray-300 rounded-xl min-h-[80px]'
                  />
                </div>

                <div>
                  <Label className='text-sm font-semibold text-gray-700 mb-2 block'>Acupuncture Points *</Label>
                  <Input
                    value={guidedForm.points_used}
                    onChange={(e) => setGuidedForm({ ...guidedForm, points_used: e.target.value })}
                    placeholder='BL23, BL40, GB34, KI3'
                    className='border-gray-300 rounded-xl'
                  />
                </div>

                <Button onClick={handleGuidedGenerate} disabled={loading} className='w-full bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-full py-6'>
                  {loading ? 'Generating...' : 'Generate Case Study'}
                </Button>
              </div>
            </TabsContent>

            <TabsContent value='full'>
              <div className='bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6'>
                <p className='text-sm text-purple-800'>
                  <strong>Full Generation:</strong> Enter minimal info (diagnosis, keyword). System creates complete comprehensive case study.
                </p>
              </div>
              <div className='space-y-6'>
                <div>
                  <Label className='text-sm font-semibold text-gray-700 mb-2 block'>Model</Label>
                  <Select value={fullForm.model} onValueChange={(value) => setFullForm({ ...fullForm, model: value })}>
                    <SelectTrigger className='w-full border-gray-300 rounded-xl'>
                      <SelectValue placeholder='Select AI model' />
                    </SelectTrigger>
                    <SelectContent>
                      {aiModels.map(model => (
                        <SelectItem key={model.value} value={model.value}>
                          {model.label} ({model.provider})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className='text-sm font-semibold text-gray-700 mb-2 block'>Difficulty</Label>
                  <div className='flex gap-4'>
                    <label className='flex items-center space-x-2 cursor-pointer'>
                      <input type='radio' value='simple' checked={fullForm.difficulty === 'simple'} onChange={(e) => setFullForm({ ...fullForm, difficulty: e.target.value })} />
                      <span className='difficulty-simple px-4 py-2 rounded-full text-sm font-medium'>Simple</span>
                    </label>
                    <label className='flex items-center space-x-2 cursor-pointer'>
                      <input type='radio' value='complex' checked={fullForm.difficulty === 'complex'} onChange={(e) => setFullForm({ ...fullForm, difficulty: e.target.value })} />
                      <span className='difficulty-complex px-4 py-2 rounded-full text-sm font-medium'>Complex</span>
                    </label>
                  </div>
                </div>

                <div>
                  <Label className='text-sm font-semibold text-gray-700 mb-2 block'>TCM Diagnosis</Label>
                  <Input
                    value={fullForm.tcm_diagnosis}
                    onChange={(e) => setFullForm({ ...fullForm, tcm_diagnosis: e.target.value })}
                    placeholder='e.g., Liver Qi Stagnation, Kidney Yang Deficiency'
                    className='border-gray-300 rounded-xl'
                  />
                </div>

                <div>
                  <Label className='text-sm font-semibold text-gray-700 mb-2 block'>Additional Info</Label>
                  <Textarea
                    value={fullForm.additional_info}
                    onChange={(e) => setFullForm({ ...fullForm, additional_info: e.target.value })}
                    placeholder='Any context, keywords, or aspects to include'
                    className='border-gray-300 rounded-xl min-h-[100px]'
                  />
                </div>

                <Button onClick={handleFullGenerate} disabled={loading} className='w-full bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-full py-6'>
                  {loading ? 'Generating...' : 'Generate Case Study'}
                </Button>
              </div>
            </TabsContent>
          </Tabs>

          {/* Show generated content */}
          {generatedContent && (
            <div className='mt-8 space-y-6 border-t pt-8'>
              <div className='bg-green-50 border border-green-200 rounded-xl p-4'>
                <p className='text-sm text-green-800'><strong>✓ Generated!</strong> Review and edit before saving.</p>
              </div>
              <div>
                <Label className='text-sm font-semibold text-gray-700 mb-2 block'>Title</Label>
                <Input value={generatedContent.title} onChange={(e) => setGeneratedContent({ ...generatedContent, title: e.target.value })} className='border-gray-300 rounded-xl' />
              </div>
              <div>
                <Label className='text-sm font-semibold text-gray-700 mb-2 block'>Description</Label>
                <Textarea value={generatedContent.description} onChange={(e) => setGeneratedContent({ ...generatedContent, description: e.target.value })} className='border-gray-300 rounded-xl min-h-[80px]' />
              </div>
              <div>
                <Label className='text-sm font-semibold text-gray-700 mb-2 block'>Content</Label>
                <Textarea value={generatedContent.content} onChange={(e) => setGeneratedContent({ ...generatedContent, content: e.target.value })} className='border-gray-300 rounded-xl min-h-[400px] font-mono text-sm' />
              </div>
              <div className='flex justify-between'>
                <Button onClick={() => setGeneratedContent(null)} variant='outline' className='rounded-full px-8'>Regenerate</Button>
                <Button onClick={handleSaveGenerated} disabled={loading} className='bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-full px-8'>
                  {loading ? 'Saving...' : 'Save Case Study'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateCaseStudyNew;